import { z } from 'zod'

import {
  permissionCatalogSchema,
  myPermissionsSchema,
  effectivePermissionsSchema,
  permissionAlreadyInheritedSchema,
  type PermissionCatalog,
  type MyPermissions,
  type EffectivePermissions,
  type PermissionAlreadyInherited,
} from '@/types/api/permission'
import {
  roleSchema,
  createRoleRequestSchema,
  updateRoleRequestSchema,
  permissionSchema,
  grantPermissionRequestSchema,
  replacePermissionsRequestSchema,
  type Role,
  type CreateRoleRequest,
  type UpdateRoleRequest,
  type Permission,
  type GrantPermissionRequest,
  type ReplacePermissionsRequest,
} from '@/types/api/role'
import { workspaceUserSchema, type WorkspaceUser } from '@/types/api/workspace'

import { apiClient } from './client'
import { ApiError } from './client'


// ── Catalog & current user ───────────────────────────────────────────────────

export async function getPermissionCatalog(): Promise<PermissionCatalog> {
  const response = await apiClient.get('/permissions/catalog')
  return permissionCatalogSchema.parse(response.data)
}

export async function getMyPermissions(): Promise<MyPermissions> {
  const response = await apiClient.get('/permissions/me')
  return myPermissionsSchema.parse(response.data)
}

/** What the current user may do with one resource (permission key values). */
export async function getAllowedActions(resourceType: string, resourceId: string): Promise<string[]> {
  const response = await apiClient.get(`/permissions/me/resources/${resourceType}/${resourceId}`)
  return z.array(z.string()).parse(response.data)
}

export async function getAllowedActionsBatch(
  resourceType: string,
  resourceIds: string[]
): Promise<Record<string, string[]>> {
  const response = await apiClient.post('/permissions/me/resources', { resourceType, resourceIds })
  return z.record(z.array(z.string())).parse(response.data)
}

export async function getEffectivePermissions(userId: string): Promise<EffectivePermissions> {
  const response = await apiClient.get(`/permissions/user/${userId}/effective`)
  return effectivePermissionsSchema.parse(response.data)
}

/** If the error is a 409 "already granted through a role", returns its details. */
export function asPermissionAlreadyInherited(error: unknown): PermissionAlreadyInherited | null {
  if (!(error instanceof ApiError) || error.status !== 409) return null
  const parsed = permissionAlreadyInheritedSchema.safeParse(error.body)
  return parsed.success ? parsed.data : null
}

// ── Role membership ──────────────────────────────────────────────────────────

export async function getRoleMembers(roleId: string): Promise<WorkspaceUser[]> {
  const response = await apiClient.get(`/roles/${roleId}/members`)
  return workspaceUserSchema.array().parse(response.data)
}

// ── Roles ────────────────────────────────────────────────────────────────────

export async function getRoles(): Promise<Role[]> {
  const response = await apiClient.get('/roles')
  return z.array(roleSchema).parse(response.data)
}

export async function getRole(roleId: string): Promise<Role> {
  const response = await apiClient.get(`/roles/${roleId}`)
  return roleSchema.parse(response.data)
}

export async function createRole(data: CreateRoleRequest): Promise<Role> {
  const validated = createRoleRequestSchema.parse(data)
  const response = await apiClient.post('/roles', validated)
  return roleSchema.parse(response.data)
}

export async function updateRole(roleId: string, data: UpdateRoleRequest): Promise<Role> {
  const validated = updateRoleRequestSchema.parse(data)
  const response = await apiClient.put(`/roles/${roleId}`, validated)
  return roleSchema.parse(response.data)
}

export async function toggleRole(roleId: string): Promise<Role> {
  const response = await apiClient.post(`/roles/${roleId}/toggle`)
  return roleSchema.parse(response.data)
}

export async function deleteRole(roleId: string): Promise<void> {
  await apiClient.delete(`/roles/${roleId}`)
}

export async function getRolePermissions(roleId: string): Promise<Permission[]> {
  const response = await apiClient.get(`/permissions/role/${roleId}`)
  return z.array(permissionSchema).parse(response.data)
}

export async function grantRolePermission(data: GrantPermissionRequest): Promise<void> {
  const validated = grantPermissionRequestSchema.parse(data)
  await apiClient.post('/permissions/grant/role', validated)
}

export async function revokeRoleGrant(roleId: string, grantId: string): Promise<void> {
  await apiClient.delete(`/permissions/role/${roleId}/grants/${grantId}`)
}

export async function replaceRolePermissions(
  roleId: string,
  data: ReplacePermissionsRequest
): Promise<void> {
  const validated = replacePermissionsRequestSchema.parse(data)
  await apiClient.put(`/permissions/replace/role/${roleId}`, validated)
}

export async function getUserPermissions(userId: string): Promise<Permission[]> {
  const response = await apiClient.get(`/permissions/user/${userId}`)
  return z.array(permissionSchema).parse(response.data)
}

/**
 * Grant a user override. Rejects with a 409 (see {@link asPermissionAlreadyInherited}) when the
 * user's roles already grant it, unless `confirmDuplicate` is set.
 */
export async function grantUserPermission(
  data: GrantPermissionRequest,
  options: { confirmDuplicate?: boolean } = {}
): Promise<void> {
  const validated = grantPermissionRequestSchema.parse(data)
  await apiClient.post('/permissions/grant/user', validated, {
    params: options.confirmDuplicate ? { confirmDuplicate: true } : undefined,
    suppressErrorToast: true,
  } as object)
}

export async function revokeUserGrant(userId: string, grantId: string): Promise<void> {
  await apiClient.delete(`/permissions/user/${userId}/grants/${grantId}`)
}

export async function replaceUserPermissions(
  userId: string,
  data: ReplacePermissionsRequest,
  options: { confirmDuplicate?: boolean } = {}
): Promise<void> {
  const validated = replacePermissionsRequestSchema.parse(data)
  await apiClient.put(`/permissions/replace/user/${userId}`, validated, {
    params: options.confirmDuplicate ? { confirmDuplicate: true } : undefined,
    suppressErrorToast: true,
  } as object)
}